import { TAU } from '../card/art/geom';
import type { CardPose } from '../card/pose';

/**
 * **打牌的手感 —— 纯数学。**
 *
 * 参考的是 Balatro：牌不是"切到新位置"，而是**一直在动**。
 * 空闲时慢慢晃，鼠标上来时立起来一点、还微微转向鼠标，
 * 按下时再压一下，拖动时带着速度上限去追指针。
 *
 * 这个文件**不 import Pixi**，所以整套手感能在 node 里单测 ——
 * 手感是那种"看着只是有点怪"的东西，靠肉眼调参效率极低。
 *
 * ## ⚠️ 单位：角度是**度**，长度是**比例**
 *
 * **角度**：`CardPose` 用的是弧度，这里是度。理由：这些数是要在调优页上拖的、
 * 也是要抄回参数表的，`1.1` 和 `0.019` 哪个是"1.1 度"没人读得出来。
 * 进机器前统一走 `rad()`，只转一次。
 *
 * **长度**：一律是**牌高（或牌宽）的比例**，不是像素。
 * 手牌在屏幕上的尺寸是会变的（张数一变、屏幕一变，牌就大不一样），
 * 写死的像素值只在调它时那个尺寸上是对的 —— 调优页上把牌调大一点，
 * "抬起 30px"立刻变成一个看不出来的小动作。
 * 所以这个文件里**没有 px**：竖着的距离按 `unit`（牌高），
 * 横着的按 `wide`（牌宽），速度按"牌高/秒"。换算只在 `feelTarget` 里做一次。
 *
 * ## 三层，别混在一起
 *
 * ```
 *   feelTarget()  交互动画：空闲摇摆 / hover / 按下 / 选中 / 避让  ← 只跟这一帧的包络有关
 *        │
 *   chase()       位置跟随：带最大速度地追目标                    ← 有状态
 *   approach()    大小跟随：单调指数趋近（**不过冲、不抖**）        ← 有状态
 * ```
 *
 * 三个都是纯函数，状态由 `HandView` 保管。
 */

/** 度 → 弧度。⚠️ 全文件只在这里转一次 */
const rad = (deg: number): number => (deg * Math.PI) / 180;

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

// ============================================================
// 参数
// ============================================================

export interface FeelParams {
  // ---- 空闲：缓慢地晃 ----
  /** 左右摇摆的幅度（度，roll） */
  idleRollAmp: number;
  /** pitch 的摆动幅度（度） */
  idlePitchAmp: number;
  /**
   * pitch 相对 roll 的**相位差**（度）。
   *
   * 90° = 两个方向差四分之一周期，合起来是**顺时针画小圈**；
   * 0° = 两个方向同相，合起来是沿着对角线来回拉。
   */
  idlePitchPhase: number;
  /** 一个完整周期多少秒 */
  idlePeriod: number;
  /** 相邻两张牌的相位差（度）。0 = 整手牌一起晃 */
  idlePhaseStep: number;

  // ---- hover ----
  /** 放大的比例。0.09 = 大 9% */
  hoverScale: number;
  /** 被 hover 那张自己再抬多少。**单位：牌高** */
  hoverLift: number;
  /** 「上下转向鼠标」的 pitch 增益（度）：指针离牌中心一个 `hoverFaceRef` 时的倾角 */
  hoverPitch: number;
  /** 「左右转向鼠标」的 yaw 增益（度）。0 = 不做左右转向 */
  hoverYaw: number;
  /** 上面那两个"一个单位"是多远。**单位：牌高** */
  hoverFaceRef: number;

  // ---- 按下 ----
  pressScale: number;
  /** **单位：牌高** */
  pressLift: number;

  // ---- 选中 ----
  /** 选中的牌抬多高。**单位：牌高** */
  selectLift: number;
  /** 选中的牌放大多少（加到乘数上） */
  selectScale: number;

  // ---- 整个手牌 ----
  /** 有任何一张被 hover 时，整手牌上移多少。**单位：牌高** */
  fanLift: number;

  // ---- 邻居避让 ----
  /** 紧邻的那张让开多少。**单位：牌宽** */
  avoid: number;
  /** 隔一张剩多少。0.55 = 每远一张衰减到 55% */
  avoidFalloff: number;

  // ---- 角度：平滑趋近 ----
  /**
   * yaw / pitch / roll 追目标的快慢（1/秒）。
   *
   * ⚠️ 这条**必须**有：位置有 `followRate` 平滑，角度如果不平滑，
   * 换位（拖动调序）时那张牌的**倾角会瞬间跳一格** —— 牌是滑过去的，
   * 倾角却是"啪"一下就到位，一眼就看出是两套东西在动。
   */
  angleRate: number;

  // ---- 大小：平滑趋近 ----
  /**
   * 大小追目标的快慢（1/秒）。
   *
   * ⚠️ 这里**故意没有用弹簧**。试过一版欠阻尼弹簧：过冲会带来"抖一两下"的
   * 手感，但评审看了说不好看。所以大小和位置一样，只做单调的指数趋近 ——
   * **不会过冲，也不会抖**。这一条是有意为之，别"顺手"改成弹簧。
   */
  scaleRate: number;

  // ---- 位置：跟随 ----
  /** 追目标的快慢（1/秒）。越大越"粘手" */
  followRate: number;
  /** **最大移动速度**（单位：**牌高/秒**）。0 = 不限速 */
  maxSpeed: number;

  // ---- 音乐脉动（Bar 事件）----
  /**
   * 脉动时所有牌一起放大多少（相对比例）。0.05 = 大 5%。
   *
   * ⚠️ 放大是**一步到位**的（"无动画地立即稍微放大"），只有缩回去有动画，
   * 所以它**不走** `scaleRate` 那条平滑 —— 见 `pulseDecay`。
   */
  barPulseScale: number;
  /** 脉动缩回去的快慢（1/秒）。指数衰减 = easeout */
  barPulseRate: number;

  // ---- 包络 ----
  /**
   * **邻居避让**进来 / 出去的快慢（1/秒）。
   *
   * ⚠️ 它和 hover / 按下的 `riseRate` / `fallRate` **分开**是有意的：
   *    评审第五轮把避让幅度从 0.24 加到 0.34 之后说"避让动画稍微慢一点，
   *    幅度大了之后会显得太快" —— 幅度和速度要能各调各的，
   *    否则调幅度就会顺手把 hover 的手感也改掉。
   */
  avoidRiseRate: number;
  avoidFallRate: number;

  /** hover / 按下**进来**的快慢（1/秒） */
  riseRate: number;
  /** **出去**的快慢（1/秒）。比进来慢一点会更"有余韵" */
  fallRate: number;
}

/**
 * **出厂手感。**
 *
 * ⚠️ 评审第四轮："卡牌相关的动画也很拖沓，将手牌扇相关的动画全部加快，令卡牌
 *    跟随拖拽的最大速度变为三倍" —— 所以这一版**整体往上抬了一档**：
 *    位置/角度/大小/包络/脉动的速率都加快了大约 1.7–2.4 倍，
 *    而 `maxSpeed`（拖拽跟随的速度上限）是**正好 3 倍**（10 → 30 牌高/秒）。
 *
 * 想再调：`/debug/hand.html` 的 ⑤⑥⑦⑧ 四个面板就是这几组的滑块
 * （位置/角度/大小/包络/脉动），改完"导出"就是这里的一份 JSON。
 */
export const DEFAULT_FEEL: FeelParams = {
  idleRollAmp: 1.1,
  idlePitchAmp: 0.9,
  idlePitchPhase: 90,
  // 11 → 8：空闲摆动快一点，读起来"在呼吸"而不是"在漂"
  idlePeriod: 8,
  idlePhaseStep: 26,

  // ⚠️ 长度全是比例：竖的按牌高、横的按牌宽、速度按牌高/秒。
  //    括号里是"150 牌高的手牌"上它等于多少像素（那只是**当时那个尺寸**）
  hoverScale: 0.09,
  hoverLift: 0.08, // 12px
  hoverPitch: 7,
  hoverYaw: 9,
  hoverFaceRef: 1.6, // 240px

  pressScale: 0.07,
  pressLift: 0.05, // 7.5px

  // 选中要一眼看得出来：抬得更明显 + 尺寸大一点，另有外发光（见 CardView.setGlow）
  selectLift: 0.4, // 60px（原 0.2）
  selectScale: 0.08, // 原 0.03

  fanLift: 0.13, // 19.5px

  // 26px → 36px（牌宽 107）：评审"hover 时其他手牌避让幅度加大一些"
  avoid: 0.34,
  avoidFalloff: 0.55,

  // 12 → 22：换位时那张牌的倾角要跟得上位置（不然"牌滑过去、角度跳一格"）
  angleRate: 22,
  // 14 → 24
  scaleRate: 24,

  barPulseScale: 0.05,
  // 7 → 12：跟着鼓点缩回去，太慢会拖到下一拍
  barPulseRate: 12,

  // 13 → 24：阻尼越强越"粘手"
  followRate: 24,
  /**
   * ★ **最大移动速度**（牌高/秒）。
   *
   * 评审第四轮："令卡牌跟随拖拽的最大速度变为三倍" —— **10 → 30**
   * （150 牌高的手牌上，1500px/s → **4500px/s**）。
   * 0 = 不限速（甩得再快也一步到位）。
   */
  maxSpeed: 30,

  // 邻居避让：慢一档（3 帧到九成 ≈ 0.25s）。幅度大了，速度就得收着点
  avoidRiseRate: 13,
  avoidFallRate: 9,

  // 16 → 26（进来） / 11 → 18（出去）
  riseRate: 26,
  fallRate: 18,
};

// ============================================================
// 包络
// ============================================================

/**
 * 指数趋近。**起步快、收尾慢**，比线性更像"亮起来 / 暗下去"。
 *
 * ⚠️ `rate` 是"每秒趋近多少"，不是时长。`rate = 16` 时大约 0.2 秒走完 95%。
 */
export function approach(current: number, target: number, dt: number, rate: number): number {
  if (dt <= 0 || rate <= 0) return current;
  const next = current + (target - current) * (1 - Math.exp(-dt * rate));
  // 够近了就吸附到目标 —— 不然会永远停在 0.998 这种值上
  return Math.abs(target - next) < 1e-4 ? target : next;
}

// ============================================================
// 音乐脉动
// ============================================================

/**
 * 一次性脉冲衰减一步。**指数下降，也就是 easeout** —— 一开始掉得快、
 * 尾巴上慢慢贴到 0。
 *
 * ⚠️ 衰减**故意不是弹簧**：Spring 会过冲，而脉动是"跟着节拍闪一下"，
 * 过冲会变成"闪两下"，和鼓点对不上。上一版手牌大小的抖动就是这么被否掉的。
 *
 * ⚠️ 上升沿也**不在这里** —— 评审要的是"无动画地立即稍微放大"，
 * 所以起点是**硬赋 1**，这个函数只管往下掉。
 */
export function pulseDecay(value: number, dt: number, rate: number): number {
  return approach(value, 0, dt, rate);
}

// ============================================================
// 位置：跟随
// ============================================================

export interface ChaseResult {
  readonly pos: readonly [number, number];
  /** 这一帧**真实**走出来的速度（逻辑像素/秒） */
  readonly vel: readonly [number, number];
}

/**
 * 朝目标追一步，**带最大速度限制**。
 *
 * 两步：
 *   1. 指数趋近 —— 起步快、收尾慢（到达时不顿）
 *   2. 如果这一步超过了 `maxSpeed · dt`，就按比例缩回去
 *
 * 第 2 步就是"鼠标太快时牌会稍微滞后"：它不是跟随变慢，
 * 而是**位置本身有速度上限**，所以甩得越快、落得越远。
 *
 * ⚠️ `maxSpeed <= 0` = 不限速。这时只有指数趋近。
 */
export function chase(
  from: readonly [number, number],
  to: readonly [number, number],
  dt: number,
  rate: number,
  maxSpeed: number,
): ChaseResult {
  if (dt <= 0) return { pos: [from[0], from[1]], vel: [0, 0] };

  const k = rate <= 0 ? 1 : 1 - Math.exp(-dt * rate);
  let dx = (to[0] - from[0]) * k;
  let dy = (to[1] - from[1]) * k;

  if (maxSpeed > 0) {
    const step = Math.hypot(dx, dy);
    const cap = maxSpeed * dt;
    if (step > cap && step > 1e-9) {
      const scale = cap / step;
      dx *= scale;
      dy *= scale;
    }
  }

  return { pos: [from[0] + dx, from[1] + dy], vel: [dx / dt, dy / dt] };
}

// ============================================================
// 交互动画
// ============================================================

export interface FeelInput {
  /** 时间（秒）。驱动空闲摇摆 */
  readonly t: number;
  /** 这张牌自己的相位偏移（弧度）。让整手牌不是一个整体在晃 */
  readonly phase: number;
  /** 这张牌在扇形里的**位次**（不是卡牌 id —— 换过顺序之后两者不一样） */
  readonly index: number;
  /** 哪一**位**正在被 hover。-1 = 没有 */
  readonly hovered: number;
  /** **这张自己**的 hover 包络 0..1 */
  readonly own: number;
  /** **这张自己**的按下包络 0..1 */
  readonly press: number;
  /**
   * **整手牌的上移包络** 0..1。
   *
   * ⚠️ 它和 `avoid` **不是一回事**，别合并（评审要的就是这个差别）：
   *
   * - `group`：整扇往上抬。评审要它**忽略 x**（只看指针的 y 在不在手牌那条
   *   横带里），而且**进是瞬时的**（「hover 手牌时的动画时间改为 0」）
   * - `avoid`：邻居往外让。它**必须继续补间** —— 评审明说了「其他牌的避让行为
   *   不受影响」。两个都挂在 `group` 上的话，把上移改成瞬时会把避让也一起
   *   变成瞬移（那正是第一版的做法）
   */
  readonly group: number;
  /** **邻居避让**的包络 0..1（有牌被指着就往外让，进/出都补间） */
  readonly avoid: number;
  /** 这张牌被选中了没有 */
  readonly selected: boolean;
  /** 指针的逻辑坐标。null = 指针不在画布上 */
  readonly pointer: readonly [number, number] | null;
  /** 换算基准：**牌高**（逻辑像素）。所有"多少牌高"的比例都乘它 */
  readonly unit: number;
  /** 换算基准：**牌宽**（逻辑像素）。横向的距离（避让）乘它 */
  readonly wide: number;
}

/**
 * 把槽位姿态 + 这一帧的交互状态，算成**目标姿态**。
 *
 * ⚠️ 返回值里的 `scale` 是**目标**大小，不是最终大小 —— 最终大小由
 * `HandView` 用 `approach()` 单调追出来（**不过冲、不抖**）。
 *
 * ## 按下时取消 hover 的转向，但不取消空闲的转向
 *
 * 评审要的是"按下去之后牌就定住了，别再看鼠标"。所以转向那一项乘
 * `(1 − press)`，而空闲摇摆（`idleRoll` / `idlePitch`）是**加在外面**的。
 */
export function feelTarget(slot: CardPose, input: FeelInput, p: FeelParams): CardPose {
  const w = TAU / Math.max(p.idlePeriod, 1e-3);
  const ph = w * input.t + input.phase;
  const idleRoll = rad(p.idleRollAmp) * Math.sin(ph);
  const idlePitch = rad(p.idlePitchAmp) * Math.sin(ph + rad(p.idlePitchPhase));

  // 转向鼠标。⚠️ 参考点用**槽位**的 x/y，不是当前动画位置 ——
  //    用后者会形成反馈环（牌被抬起来 → 偏移变了 → 又抬一点 → ……）
  //
  // ⚠️ 符号：`pitch` 正值 = 上边向后倒，`yaw` 正值 = 右边向后倒。
  //    指针在牌**上面** → `slot.y − pointer.y > 0` → +pitch → 牌面朝上；
  //    指针在牌**右边** → `pointer.x − slot.x > 0` → +yaw → 牌面朝右。两边一致
  // ⚠️ 比例 → 像素只在这里换一次。`unit` = 牌高、`wide` = 牌宽
  const unit = input.unit;
  const ref = Math.max(p.hoverFaceRef * unit, 1);
  const face = input.own * (1 - input.press);
  const facePitch =
    input.pointer === null
      ? 0
      : rad(p.hoverPitch) * clamp((slot.y - input.pointer[1]) / ref, -1, 1) * face;
  const faceYaw =
    input.pointer === null
      ? 0
      : rad(p.hoverYaw) * clamp((input.pointer[0] - slot.x) / ref, -1, 1) * face;

  // 邻居避让：往**远离**被 hover 那一位的方向挪，按距离衰减
  const d = input.hovered < 0 ? 0 : input.index - input.hovered;
  const shift =
    d === 0
      ? 0
      : Math.sign(d) *
        p.avoid *
        input.wide *
        Math.pow(p.avoidFalloff, Math.abs(d) - 1) *
        input.avoid;

  const lift =
    (p.fanLift * input.group +
      p.hoverLift * input.own +
      p.pressLift * input.press +
      (input.selected ? p.selectLift : 0)) *
    unit;

  return {
    x: slot.x + shift,
    y: slot.y - lift,
    scale:
      slot.scale * (1 + p.hoverScale * input.own + p.pressScale * input.press) +
      (input.selected ? p.selectScale : 0),
    yaw: slot.yaw + faceYaw,
    pitch: slot.pitch + idlePitch + facePitch,
    roll: slot.roll + idleRoll,
    perspective: slot.perspective,
  };
}

/**
 * 这张牌自己的空闲相位偏移。
 *
 * ⚠️ 不用 `Math.random`：同一个位次必须永远得到同一个相位，
 * 否则刷新一下整手牌就换了个晃法，调参时根本判断不了改的是哪个数。
 */
export function idlePhaseOf(index: number, p: FeelParams): number {
  return rad(p.idlePhaseStep) * index;
}
